import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { ErrorList } from "@/components/erp/UploadCard";
import api, { apiError } from "@/lib/api";
import { coverage, ERP_TYPES, omanTime, STATUS_CLS } from "@/lib/erp";
import { cn } from "@/lib/utils";

const Detail = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { if (id) api.get(`/erp/uploads/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); else setD(null); }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="upload-detail-dialog">
        <DialogHeader><DialogTitle>{d ? `${d.filename} · ${ERP_TYPES[d.type]}` : "Loading…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div>Status: <span className={cn("rounded px-1.5 py-0.5 text-xs font-semibold", STATUS_CLS[d.status])}>{d.status}</span></div>
              <div>Uploaded: {omanTime(d.uploaded_at)} by {d.uploaded_by}</div>
              <div>Range: {coverage(d)}</div>
              <div>Imported {d.imported ?? 0} · replaced {d.replaced ?? 0} · skipped {d.skipped ?? 0}</div>
            </div>
            {d.warnings?.map((w) => <p key={w} className="text-xs text-amber-700">{w}</p>)}
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Errors ({d.error_count ?? 0})</div>
            {d.errors?.length ? <ErrorList errors={d.errors} total={d.error_count} testid="upload-detail-errors" /> : <p className="text-xs text-slate-500" data-testid="upload-detail-no-errors">No errors.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function UploadHistoryPage() {
  const [type, setType] = useState("all");
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/erp/uploads", { params: type === "all" ? {} : { type } }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [type]);
  return (
    <div className="space-y-6" data-testid="upload-history-page">
      <PageHeader eyebrow="Data" title="Upload History" subtitle="Every upload, newest first · times in Oman (GST, UTC+4)"
        actions={(
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-44 bg-white" data-testid="history-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(ERP_TYPES).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`history-type-${k}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        )} />
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="history-table">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>{["File", "Type", "Uploaded at", "By", "Range / as of", "Imported", "Replaced", "Skipped", "Status"].map((h, i) => <th key={h} className={cn("whitespace-nowrap px-4 py-3", i >= 5 && i <= 7 ? "text-right" : "text-left")}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows?.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No uploads yet.</td></tr>}
            {rows?.map((u) => (
              <tr key={u._id} onClick={() => setOpen(u._id)} className="row-hover cursor-pointer border-t border-slate-100" data-testid={`history-row-${u._id}`}>
                <td className="px-4 py-2.5 font-medium text-slate-800">{u.filename}</td>
                <td className="px-4 py-2.5">{ERP_TYPES[u.type]}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{omanTime(u.uploaded_at)}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{u.uploaded_by}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-xs text-slate-600">{coverage(u)}</td>
                <td className="num px-4 py-2.5 text-right">{(u.imported ?? 0).toLocaleString()}</td>
                <td className="num px-4 py-2.5 text-right">{(u.replaced ?? 0).toLocaleString()}</td>
                <td className="num px-4 py-2.5 text-right">{(u.skipped ?? 0).toLocaleString()}</td>
                <td className="px-4 py-2.5"><span className={cn("rounded px-2 py-0.5 text-[11px] font-semibold", STATUS_CLS[u.status])} data-testid={`history-status-${u._id}`}>{u.status}</span>
                  {u.error_count > 0 && <span className="ml-2 text-[11px] text-red-600">{u.error_count} {u.error_count === 1 ? "error" : "errors"}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Detail id={open} onClose={() => setOpen(null)} />
    </div>
  );
}
